import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ArrowClockwise, ArrowSquareOut } from "@phosphor-icons/react";
import { useLocation } from "wouter";
import type { Application, AppViewStatus, Funnel, Job, Outcome } from "../../shared/types.ts";
import { APP_STATUS, AppStatusBadge, Button, EmptyState, IconButton, List, ListRow, Monogram, Page, Sankey, Segmented, SourceLogo, cn, softSpring, type Tone } from "../ui/index.ts";
import { errorText, getApplications, getFunnel, isJob, retry, setOutcome, webUrl } from "../lib/api.ts";
import { useApp } from "../lib/ctx.ts";
import { ago, pay, place, utc, WORKPLACE } from "../lib/format.ts";
import { shareSankey } from "../lib/share-sankey.ts";
import { Detail } from "./Detail.tsx";
import { OutcomeMenu } from "./Outcome.tsx";
import { Loading } from "./parts.tsx";
import { SENT, SentList } from "./SentList.tsx";

type Tab = "all" | "sent" | "needs";

/** Sent and out of the engine's hands, so something can come back. */
const MARKABLE = new Set<AppViewStatus>(["submitted", "confirmed", "on_site"]);

const inTab = (t: Tab, s: AppViewStatus) => t === "all" || (t === "needs" ? s === "failed" : SENT.has(s));

const EMPTY: Record<Tab, string> = {
  all: "Nothing sent yet.",
  sent: "Nothing sent yet.",
  needs: "Nothing stopped.",
};

/** What a row's time means for its status. */
const VERB: Record<AppViewStatus, string> = {
  pending: "Queued",
  queued: "Queued",
  applying: "Sending",
  submitted: "Sent",
  confirmed: "Confirmed",
  failed: "Stopped",
  skipped: "Skipped",
  on_site: "Applied",
};

/** Status text colour on phones, where the badge folds into the meta line. */
const INK: Record<Tone, string> = {
  brand: "text-tone-brand-ink",
  mint: "text-tone-mint-ink",
  accent: "text-tone-accent-ink",
  cream: "text-tone-cream-ink",
  amber: "text-tone-amber-ink",
  gray: "text-tone-gray-ink",
  red: "text-tone-red-ink",
};

const stamp = (r: Application) => utc(r.submittedAt ?? r.updatedAt) || 0;

/** The funnel after one application's outcome moves from `from` to `to`, so the chart answers before the server does. */
function shift(f: Funnel, from: Outcome | null, to: Outcome | null): Funnel {
  const next = { ...f };
  const apply = (o: Outcome | null, d: number) => {
    if (o === null) next.noReply += d;
    else if (o === "rejected") next.rejected += d;
    else {
      next.interviews += d;
      if (o === "offer") next.offers += d;
      if (o === "no_offer") next.noOffer += d;
    }
  };
  apply(from, -1);
  apply(to, 1);
  return next;
}

export function Applications() {
  const app = useApp();
  const [, navigate] = useLocation();
  const [rows, setRows] = useState<Application[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState<Tab>("all");
  const [open, setOpen] = useState<Job | null>(null);
  const [funnel, setFunnel] = useState<Funnel | null>(null);
  const funnelAt = useRef(0);

  const loadFunnel = useCallback(() => {
    const n = ++funnelAt.current;
    getFunnel().then(
      (f) => n === funnelAt.current && setFunnel(f),
      () => {},
    );
  }, []);

  const load = useCallback(() => {
    loadFunnel();
    getApplications().then(
      (r) => {
        setRows([...r].sort((a, b) => stamp(b) - stamp(a)));
        setFailed(false);
      },
      () => setFailed(true),
    );
  }, [loadFunnel]);

  useEffect(() => {
    load();
    addEventListener("engine-event", load);
    return () => removeEventListener("engine-event", load);
  }, [load]);

  const busy = rows?.some((r) => r.status === "queued" || r.status === "applying");
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [busy, load]);

  const again = (id: string) => {
    setRows((rs) => rs?.map((r) => (r.job.id === id ? { ...r, status: "queued", reasonLabel: "" } : r)) ?? null);
    retry(id).then(
      () => {
        app.refreshStats();
        loadFunnel();
      },
      (e) => {
        app.toast(errorText(e, "Retry didn't go through."), "bad");
        load();
      },
    );
  };

  const mark = (id: string, outcome: Outcome | null) => {
    const before = rows?.find((r) => r.job.id === id)?.outcome ?? null;
    if (before === outcome) return;
    const ours = (r: Application) => r.job.id === id && r.outcome === outcome;
    const move = (from: Outcome | null, to: Outcome | null) => {
      funnelAt.current++;
      setFunnel((f) => f && shift(f, from, to));
    };
    setRows((rs) => rs?.map((r) => (r.job.id === id ? { ...r, outcome } : r)) ?? null);
    move(before, outcome);
    setOutcome(id, outcome).then(
      (v) => {
        setRows((rs) => rs?.map((r) => (ours(r) ? { ...r, ...v, job: r.job } : r)) ?? null);
        loadFunnel();
      },
      (e) => {
        setRows((rs) => rs?.map((r) => (ours(r) ? { ...r, outcome: before } : r)) ?? null);
        move(outcome, before);
        loadFunnel();
        app.toast(errorText(e), "bad");
      },
    );
  };

  const count = (t: Tab) => rows?.filter((r) => inTab(t, r.status)).length ?? 0;
  const shown = rows?.filter((r) => inTab(tab, r.status)) ?? [];

  return (
    <Page title="Your applications.">
      {failed && !rows ? (
        <EmptyState
          title="Your applications didn't load."
          action={
            <Button variant="secondary" onClick={load}>
              Try again
            </Button>
          }
        />
      ) : !rows ? (
        <Loading label="Loading your applications" />
      ) : !rows.length ? (
        <EmptyState
          art="plane"
          title="Nothing sent yet."
          text="Apply to a job and it shows up here."
          action={<Button onClick={() => navigate("/")}>See today's matches</Button>}
        />
      ) : (
        <>
          {funnel && funnel.sent > 0 && <Search funnel={funnel} rows={rows} />}
          <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="flex min-w-0 flex-col gap-3">
              <div className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:px-0">
                <Segmented
                  aria-label="Applications"
                  value={tab}
                  onChange={setTab}
                  className="max-w-none"
                  options={[
                    { value: "all", label: "All", count: rows.length },
                    { value: "sent", label: "Sent", count: count("sent") },
                    { value: "needs", label: "Stopped", count: count("needs"), tone: "bad" },
                  ]}
                />
              </div>
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={tab}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4, transition: { duration: 0.1 } }}
                  transition={softSpring}
                >
                  {shown.length ? (
                    <List>
                      {shown.map((r) => (
                        <Row key={r.job.id} r={r} onRetry={again} onOpen={setOpen} onOutcome={mark} />
                      ))}
                    </List>
                  ) : (
                    <div className="rounded-card shadow-card">
                      <EmptyState compact art={tab === "needs" ? undefined : "plane"} title={EMPTY[tab]} />
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
            <SentList
              rows={rows}
              onOpen={setOpen}
              className="max-lg:hidden lg:sticky lg:top-8 lg:-m-2 lg:max-h-[calc(100dvh-4rem)] lg:overflow-y-auto lg:overscroll-contain lg:p-2 lg:[scrollbar-width:thin]"
            />
          </div>
        </>
      )}
      <Detail job={open} onClose={() => setOpen(null)} short />
    </Page>
  );
}

/** The whole search as a Sankey, with a line on how it's going and a button to share it as an image. */
function Search({ funnel, rows }: { funnel: Funnel; rows: Application[] }) {
  const app = useApp();
  const [busy, setBusy] = useState(false);
  const marked = funnel.sent - funnel.noReply;
  const rate = Math.round((funnel.interviews / funnel.sent) * 100);
  const share = () => {
    const first = Math.min(...rows.map((r) => utc(r.createdAt) || Date.now()));
    setBusy(true);
    shareSankey(funnel, { since: new Date(first) })
      .then(
        (r) => r === "downloaded" && app.toast("Saved to your downloads.", "ok"),
        () => app.toast("The image didn't save. Try again.", "bad"),
      )
      .finally(() => setBusy(false));
  };
  return (
    <section className="rounded-card bg-surface shadow-card">
      <header className="flex items-center justify-between gap-3 px-4 pt-3.5 sm:px-5">
        <p className="min-w-0 text-[13.5px] text-ink-soft">
          {!marked ? (
            "Mark what came back on each application."
          ) : funnel.interviews ? (
            <>
              <span className="font-bold text-ink tabular-nums">{rate}%</span> of what you sent got an interview.
            </>
          ) : (
            "No interviews yet."
          )}
        </p>
        <Button variant="secondary" size="sm" loading={busy} onClick={share}>
          Share
        </Button>
      </header>
      <div className="px-4 pb-4 pt-3 sm:px-5 sm:pb-5">
        <Sankey funnel={funnel} />
      </div>
    </section>
  );
}

function Row({
  r,
  onRetry,
  onOpen,
  onOutcome,
}: {
  r: Application;
  onRetry: (id: string) => void;
  onOpen: (j: Job) => void;
  onOutcome: (id: string, o: Outcome | null) => void;
}) {
  const j = r.job;
  const job = isJob(j) ? j : null;
  const reason = r.reasonLabel && (r.status === "failed" || r.status === "skipped") ? r.reasonLabel : null;
  const site = job && !job.closed ? (webUrl(job.applyUrl) ?? webUrl(job.jobUrl)) : null;
  const money = job ? pay(job) : null;
  const where = job ? place(job) : null;
  const wp = job ? WORKPLACE[job.workplace] : undefined;
  const time = ago(r.submittedAt ?? r.updatedAt);
  const acts = r.status === "failed";
  const markable = MARKABLE.has(r.status);
  return (
    <ListRow
      className={cn(r.status === "failed" && "bg-tone-red/40")}
      leading={<Monogram name={job?.company ?? "?"} url={job?.jobUrl} />}
      title={job?.title ?? "Removed posting"}
      meta={
        <>
          {!markable && <span className={cn("font-semibold sm:hidden", INK[APP_STATUS[r.status].tone])}>{`${APP_STATUS[r.status].label} · `}</span>}
          {reason && <span className={r.status === "failed" ? "font-semibold text-bad-ink" : "font-semibold"}>{`${reason} · `}</span>}
          {job && <span className="font-semibold text-ink-soft">{job.company}</span>}
          {where && ` · ${where}`}
          {wp && where !== wp && ` · ${wp}`}
          <span className="md:hidden">{time && ` · ${time}`}</span>
        </>
      }
      onClick={job ? () => onOpen(job) : undefined}
      disabled={!job}
      chevron={!acts && !!job}
      trailing={
        <>
          <span className="hidden w-24 text-right md:block">
            <span className="block text-[13px] font-bold text-ink tabular-nums">{money ?? <span className="font-normal text-muted">Pay not listed</span>}</span>
            {job && (
              <span className="mt-0.5 flex justify-end">
                <SourceLogo source={job.source} size="xs" withName className="gap-1.5 text-[12px] font-medium text-muted" />
              </span>
            )}
          </span>
          <span className="hidden w-28 text-right md:block">
            <span className="block text-[13px] text-ink-soft">{VERB[r.status]}</span>
            <span className="mt-0.5 block text-[12px] text-muted tabular-nums">{time}</span>
          </span>
          {!markable && (
            <span className={cn("hidden justify-end sm:flex", acts ? "w-32" : "w-[182px]")}>
              <AppStatusBadge status={r.status} />
            </span>
          )}
        </>
      }
      actions={
        markable ? (
          <span className="flex justify-end sm:w-[176px]">
            <OutcomeMenu status={r.status} outcome={r.outcome} company={job?.company ?? "this job"} onChange={(o) => onOutcome(j.id, o)} />
          </span>
        ) : acts ? (
          <span className="flex items-center justify-end gap-1.5 sm:w-[70px]">
            <IconButton label={`Retry ${job?.company ?? "this application"}`} size="sm" variant="secondary" onClick={() => onRetry(j.id)}>
              <ArrowClockwise size={16} weight="bold" aria-hidden />
            </IconButton>
            {site && (
              <a
                href={site}
                target="_blank"
                rel="noreferrer"
                aria-label={`Apply to ${job!.company} on site`}
                title="Apply on site"
                className="hidden size-8 shrink-0 place-items-center rounded-control text-muted transition-[background-color,color] duration-150 hover:bg-ink/[0.05] hover:text-ink sm:grid"
              >
                <ArrowSquareOut size={16} weight="bold" aria-hidden />
              </a>
            )}
          </span>
        ) : undefined
      }
    />
  );
}
